import Storage from "@/services/storage";
import {
  OrderCreatorInterface,
  OrderInterface,
  OrderSaleInterface,
  validateOrderInterface,
} from "@/interface/order.inteface";
import { ItemInterface, OrderNotesInterface, PriceInterface } from "@/interface/business/item.interface";
import { OrderOptionType, OrderType } from "@/types/orders";
import { PaymentMethodInterface } from "@/interface/business/payment.interface";
import { OrderOptionsInterface } from "@/interface/business/order.interface";
import { PaymentMethod } from "@/modules/business/payment-method";
import { PaymentMethodType } from "@/types/business/payment.type";
import { StorageConstants } from "@/enums/storage.enum";
import {
  DISCOUNT_APPLIED_ON,
  ORDER_OPTION_TYPE,
  ORDER_PAYMENT_STATUS,
  ORDER_SOURCE,
  ORDER_STATUS,
  ORDER_STEPS,
} from "@/enums/orders.enum";
import { OrderCreator, OrderSale } from "./index";
import { $vm } from "@/components";
import { Helper } from "@/services/helper";
import { ItemDiscount, OrderNotes, OrderOption, PriceMoney } from "@/modules/location";
import { DiscountInterface } from "@/interface/business/discount.interface";
import moment from "moment-timezone";
import { DISCOUNT_TYPES, TAXES_INCLUSION_TYPE } from "@/enums/business.enum";
import Discount from "@/modules/business/discount/discount";
import { FORM_TYPE } from "@/enums/apps.enum";
import { ExternalFess } from "@/modules/orders/external-fess";
import { Translate as $t } from "@/plugins/translate";
import { SELECTED_ADDRESS } from "@/enums/address.enum";

export default class Order implements OrderInterface {
  public readonly id: string = "";
  public name: string = "";
  public orderName: string = "";
  public readonly ticket_number?: string = "";
  public issuer_id: string = "";
  public creation_time: string = "";
  public end_time?: string = "";
  public status: string = "";
  public total_amount: number = 0;
  public creator?: OrderCreatorInterface = {};
  public sale: OrderSaleInterface;
  public app_id: string = "";
  public location_id: string = "";
  public notes: OrderNotesInterface;
  public payment_methods: Array<PaymentMethodInterface>;
  public category?: string = "";
  public template?: Object;
  public order_option: OrderOptionsInterface;
  public custom_amount: string = "";
  public pickup?: Object;
  public comments: string = "";
  public statusLabel?: any = null;
  public payment_status?: string = "";
  public paymentLabel?: any = null;
  public isCalculating?: boolean = false;
  public isFailed?: boolean = false;
  public isReorder: boolean = false;
  public retryCount: number = 0;
  public source: string = "";
  public failedTime: string = "";
  public deliveryFeesAmount: any = null;
  public total: PriceInterface = new PriceMoney({ amount: 0 });
  public additive_tax: PriceInterface = new PriceMoney({ amount: 0 });
  public inclusive_tax: PriceInterface = new PriceMoney({ amount: 0 });
  public totalDiscount: PriceInterface = new PriceMoney({ amount: 0 });
  public totalExtraCharge: PriceInterface = new PriceMoney({ amount: 0 });
  public discount?: DiscountInterface;
  public saleDiscountType: string = "";
  public formType: string = FORM_TYPE.CREATE;
  public step: number = ORDER_STEPS.INIT;
  public orderNumber: number = 0;
  public isTagDiscountConfirmed: boolean = false;
  public transaction_id: string = "";

  constructor(data: OrderType) {
    this.id = data.id ?? Helper.createUUID();
    this.ticket_number = data.ticket_number || "";
    this.name = data.name ?? "CC-#1234";
    this.orderName = data.name?.split("-").pop() ?? "";
    this.creation_time = moment(data.creation_time).toISOString();

    this.location_id = data.location_id;
    this.end_time = data.end_time;
    this.status = data.status || ORDER_STATUS.DRAFT;
    this.app_id = data?.app_id ?? "00000000-0000-4000-0000-000000000000";

    this.category = data.category;
    this.total_amount = data.total_amount;
    this.template = data.template;
    this.custom_amount = data.custom_amount;
    this.pickup = data.pickup;
    this.comments = data.comments;
    this.source = data.source || ORDER_SOURCE.CALLCENTER;

    this.total = new PriceMoney({ amount: this.total_amount });
    this.payment_methods = data.payment_methods?.map((methods: PaymentMethodType) => new PaymentMethod(methods, true));
    this.creator = new OrderCreator(data.creator);
    this.order_option = new OrderOption(data.order_option);
    this.sale = new OrderSale(data.sale);
    this.notes = new OrderNotes(data.notes);

    this.isFailed = data?.isFailed || false;
    this.retryCount = data?.retryCount || 0;
    this.failedTime = data?.failedTime || "";
    this.transaction_id = data?.transaction_id || "";

    this.setOrderCreator();
    this.setStatusLabel();
    this.setPaidLabel();
    this.setIssuerId();

    if (data?.discount) {
      this.discount = new Discount(data.discount);
    }

    if (data.id) {
      this.isTagDiscountConfirmed = true;
      this.deliveryFeesAmount = data?.sale?.external_fees?.[0]?.amount;
    }
  }

  /**
   * To reset order data when excuting reorder function
   * @return {void}
   */
  public initReorderData() {
    this.isReorder = true;
    this.orderName = "";
    this.status = ORDER_STATUS.PENDING;
    this.creation_time = moment().toISOString();
    this.notes = new OrderNotes();
    if (this.sale.delivery) {
      this.sale.delivery = null;
    }
  }

  /**
   * To set location_id for order
   * @param {string} location_id
   * @return {void}
   */
  public setOrderLocationId(location_id: string): void {
    this.location_id = location_id;
    this.isTagDiscountConfirmed = false;
  }

  /**
   * To set location_id for order
   * @return {string}
   */
  public getOrderLocationId(): string {
    return this.location_id;
  }

  /**
   * To set customer in sale
   * @param {CustomerType} customer
   * @return {void}
   */
  public setCustomer(customer): void {
    this.sale.customer = customer;
  }

  /**
   * To is order sale delivery
   * @return {void}
   */
  public isOrderOptionDelivery(): boolean {
    return this.sale?.order_option?.type === ORDER_OPTION_TYPE.DELIVERY;
  }

  /**
   * To reset customer object in sale options
   * @return {void}
   */
  public resetCustomer(): void {
    this.sale.customer = undefined;
  }

  /**
   * Get boolean customer selected or not
   * @return {boolean}
   */
  public isCustomerSelected(): boolean {
    return !!this.sale.customer;
  }

  /**
   * Get boolean order ready to calculate or store
   * @return {boolean}
   */
  public isOrderReady(): boolean {
    return !!this.sale?.customer && !!this.sale?.items?.length;
  }

  /**
   * To set item in sale items
   * @param {ItemInterface} item
   * @return {void}
   */
  public setSaleOrderItems(item: ItemInterface): void {
    let isEqual = false;

    this.sale.items.some((oItem) => {
      if (oItem.isEqual(item)) {
        isEqual = true;
        oItem.updateQuantities(item);
      }
      return isEqual;
    });

    if (!isEqual) {
      this.sale.items.push(item);
    }
  }
  /**
   * To reset sale items discounts if the sale discount null
   * @return {void}
   */
  public resetItemsDiscountsAppliedSale(): void {
    this.sale.items?.map((item) => {
      if (item?.discount && item?.discount?.applied_on === DISCOUNT_APPLIED_ON.SALE) {
        item.discount = undefined;
      }
      return item;
    });
  }

  /**
   * To remove item in sale items
   * @param {ItemInterface} item
   * @return {void}
   */
  public removeSaleOrderItem(item: ItemInterface): void {
    const itemIndex = this.sale.items.findIndex((saleItem: ItemInterface) => saleItem.id === item.id);
    this.sale.items.splice(itemIndex, 1);
  }

  /**
   * To remove item in sale items
   * @return {void}
   */
  public clearSaleOrderItems(): void {
    this.sale.items = [];
  }

  /**
   * To setup the delivery fees amount
   * @return {void}
   */
  public setDeliveryFeesAmount(amount: any = null): void {
    this.deliveryFeesAmount = amount;
  }

  /**
   * To setup the order discount
   * @return {void}
   */
  public setOrderDiscount(discount: DiscountInterface): void {
    this.discount = discount;
    if (!this.discount) {
      return;
    }
    // update the discount applied_on SALE
    const newItemDiscount = new ItemDiscount(null);
    Object.assign(newItemDiscount, discount);
    this.sale.items.map((item) => {
      if ((item?.discount && item?.discount?.applied_on === DISCOUNT_APPLIED_ON.SALE) || !item?.discount) {
        item.discount = newItemDiscount;
      }
    });
  }

  /**
   * To setup the order discount
   * @return {void}
   */
  public updateSaleDiscountValue(): void {
    if (!this.discount) {
      return;
    }
    this.sale.items.map((item) => {
      if (item?.discount && item?.discount?.applied_on === DISCOUNT_APPLIED_ON.SALE) {
        if (this.discount?.type === DISCOUNT_TYPES.PERCENTAGE) {
          item.discount.rate = this.discount.rate;
        }

        if (this.discount?.type === DISCOUNT_TYPES.FIXED) {
          item.discount.amount = this.discount.amount;
        }
      }
    });
  }

  /**
   * To edit item in sale items
   * @param {ItemInterface} item
   * @return {void}
   */
  public editSaleOrderItems(item: ItemInterface): void {
    const itemIndex = this.sale.items.findIndex((saleItem: ItemInterface) => saleItem.id === item.id);
    // reset sale item id on update
    this.sale.items[itemIndex] = item;
    this.sale.items[itemIndex].resetSaleItemId();
  }

  /**
   * To reset sale items
   * @return {void}
   */
  public resetSaleItems(): void {
    this.sale.items = [];
  }

  /**
   * To set item in orderOption
   * @param {OrderOptionType} orderOption
   * @return {void}
   */
  public setSaleOrderOption(orderOption?: OrderOptionType): void {
    if (orderOption) {
      this.sale.order_option = new OrderOption(orderOption);
    } else {
      this.sale.order_option = null;
    }
  }

  /**
   * To set delivery date time
   * @param {any} dataTime
   * @return {void}
   */
  public setDeliveryTime(dataTime: any): void {
    this.notes.delivery_time = dataTime?.isValid() ? dataTime.format("HH:mm") : "";
    this.notes.delivery_date = dataTime?.isValid() ? dataTime.format("YYYY-MM-DD") : "";
  }

  /**
   * To set order creator
   * @return {void}
   */
  public setOrderCreator(): void {
    const user = Storage.get(StorageConstants.SESSION);
    this.creator = new OrderCreator(user);
  }

  /**
   * To set order creator
   * @return {void}
   */
  public setIssuerId(): void {
    this.issuer_id = "00000000-0000-0002-0002-000000000002" || this.creator?.id || "";
  }

  /**
   * To set order payment method
   * @param {any} paymentMethod
   * @return {void}
   */
  public setOrderPaymentMethod(paymentMethod: Array<PaymentMethodType>): void {
    this.payment_methods = paymentMethod.map((paymentMethod) => new PaymentMethod(paymentMethod));
  }

  /**
   * To set customer in sale
   * @return {void}
   */
  public resetSaleOptions(): void {
    this.setOrderLocationId("");
    this.resetCustomer();
    this.resetSaleItems();
  }

  /**
   * To set order steps
   * @return {void}
   */
  public setOrderStep(step: number): void {
    this.step = step;
  }

  /**
   * Is Order Create Form type
   * @return {Boolean}
   */
  public isCreateForm(): boolean {
    return this.formType === FORM_TYPE.CREATE;
  }

  /**
   * Is Order Preview Form type
   * @return {Boolean}
   */
  public isPreviewForm(): boolean {
    return this.formType === FORM_TYPE.PREVIEW;
  }

  /**
   * Is Order Edit Form type
   * @return {Boolean}
   */
  public isEditForm(): boolean {
    return this.formType === FORM_TYPE.EDIT;
  }

  /**
   * Get total amount
   * return {String}
   */
  public getTotalAmount() {
    const { currency } = Storage.get(StorageConstants.BUSINESS);
    const total: string = (this.total_amount / 1000).toString();
    return `${parseInt(total).toFixed(3)} ${currency}`;
  }

  /***
   * handle on validate order before submit
   * @return {any: true if}
   */
  public validate(): validateOrderInterface {
    const validate: validateOrderInterface = {
      success: true,
    };

    const customerAddress = this.sale.customer?.addresses.find((address) => address.selected);
    // validate customer address
    if (this.isOrderOptionDelivery() && (!customerAddress || customerAddress === SELECTED_ADDRESS.NO_ADDRESS)) {
      validate.success = false;
      validate.message = $t("customer_address_required");
      validate.key = "customer_address";
      return validate;
    }

    // validate discount value when its custom
    if (!!this?.discount && this?.discount?.is_custom && !this.discount?.rate && !this.discount?.amount) {
      validate.success = false;
      validate.key = "discount_value";

      if (this.discount?.type === DISCOUNT_TYPES.PERCENTAGE) {
        validate.message = `${$t("validate_percent_number_value")}`;
      }

      if (this.discount?.type === DISCOUNT_TYPES.FIXED) {
        validate.message = `${$t("validate_number_value")}`;
      }

      return validate;
    }

    // validate delivery fees
    if (this.isOrderOptionDelivery() && !!window._.isNull(this.deliveryFeesAmount)) {
      validate.success = false;
      validate.message = $t("confirm_delivery_fees_error_message");
      validate.key = "delivery_fees";
      return validate;
    }

    return validate;
  }

  /**
   * To enhance the status label
   * @private
   * return {Object}
   */
  private setStatusLabel() {
    switch (this.status) {
      case ORDER_STATUS.DRAFT:
        this.statusLabel = { class: "humming--text", text: $vm.$t("draft"), icon: "completed.svg" };
        break;

      case ORDER_STATUS.CLOSED:
        this.statusLabel = { class: "humming--text", text: $vm.$t("close"), icon: "completed.svg" };
        break;

      case ORDER_STATUS.CANCELED:
        this.statusLabel = { class: "humming--text", text: $vm.$t("cancel"), icon: "completed.svg" };
        break;

      case ORDER_STATUS.IN_TRANSIT:
        this.statusLabel = {
          class: "provincial_pink--text",
          text: $vm.$t("in_progress"),
          icon: "in_progress.svg",
        };
        break;

      case ORDER_STATUS.OPENED:
        this.statusLabel = { class: "cyan--text", text: $vm.$t("open"), icon: "open.svg" };
        break;

      case ORDER_STATUS.REJECTED:
        this.statusLabel = { class: "humming--text", text: $vm.$t("rejected"), icon: "completed.svg" };
        break;

      case ORDER_STATUS.VOIDED:
        this.statusLabel = { class: "carousel--text", text: $vm.$t("voided"), icon: "refunded.svg" };
        break;

      case ORDER_STATUS.PENDING:
      default:
        this.statusLabel = { class: "humming--text", text: $vm.$t("pending"), icon: "completed.svg" };
        break;
    }
  }

  /**
   * To enhance the status label
   * @private
   * return {Object}
   */
  public setPaidLabel() {
    switch (this.payment_status) {
      case ORDER_PAYMENT_STATUS.PARTIALLY_REFUNDED:
        this.paymentLabel = { class: "warning--text", text: $vm.$t("partially_refunded") };
        break;

      case ORDER_PAYMENT_STATUS.PAID:
        this.paymentLabel = { class: "success--text", text: $vm.$t("paid") };
        break;

      case ORDER_PAYMENT_STATUS.VOIDED:
        this.paymentLabel = { class: "error--text", text: $vm.$t("voided") };
        break;

      case ORDER_PAYMENT_STATUS.PARTIALLY_PAID:
      default:
        this.paymentLabel = { class: "dark--text", text: $vm.$t("partially_paid") };
        break;
    }
  }

  /**
   * Get selected Customer
   * @return {any}
   */
  public getSelectedCustomer() {
    const customer = this.sale.customer;
    const phone = customer?.phone_numbers.find((phone) => phone.selected);
    phone.is_primary = true;
    const address = customer?.addresses.find((address) => address.selected);
    return {
      id: customer?.id || "",
      first_name: customer?.first_name || "",
      last_name: customer?.last_name || "",
      email: customer?.email || "",
      gender: customer?.gender || "",
      dob: customer?.dob || null,
      country: customer?.country,
      phone,
      address,
    };
  }

  /**
   * Create format
   * @param {Boolean} isFormNow
   * @return {string}
   */
  creationDateTime(isFormNow: boolean = false): string {
    let creation_time = window.moment.utc(this.creation_time);
    if (isFormNow) return creation_time.fromNow(false);

    return creation_time.format("YYYY-MM-DD: HH:i");
  }

  /**
   * is Has odd discount
   * @return {boolean}
   */
  public hasOddDiscount(): boolean {
    if (!!this.discount) return false;

    const isFixed = this.sale?.items?.findIndex((item) => item.discount?.type === DISCOUNT_TYPES.FIXED) >= 0;
    // const isPercentage = this.sale?.items?.findIndex((item) => item.discount?.type === DISCOUNT_TYPES.PERCENTAGE) >= 0;
    // return isPercentage && isFixed;
    return isFixed && this.sale?.items?.length > 1;
  }

  /**
   * Prepare the Delivery Fees
   * @private
   */
  private getDeliveryFeesAmountValue(): number {
    let value = this.deliveryFeesAmount?.toString()?.replace(/\D+/g, "") || 0;
    // value = parseInt(`${value}`) * 1000;
    // return value;
    return parseInt(`${value}`);
  }

  /**
   * get default location
   * @return {string}
   */
  public getBusinessDefaultLocation(): string {
    const locations = Storage.get(StorageConstants.LOCATIONS);
    const defaultLocation = locations.find((location) => location.is_default) || null;

    return defaultLocation?.id || locations?.[0].id;
  }

  /**
   * get customer address area id
   * @return {string}
   */
  public getCustomerAddressAreaId(): string {
    const addresses = this.sale.customer?.addresses || [];
    const selectedAddress = addresses.find((address) => !!address.selected);
    return selectedAddress?.area?.id || selectedAddress?.area_id || "";
  }

  /**
   * Get Tab object
   * return {any}
   */
  public toTab(): any {
    // set discounts ;
    this.setSuitableDiscount();

    // const has_odd_discount = this.hasOddDiscount();

    let items = this.sale.items.map((item) => {
      let has_odd_discount = item?.discount?.applied_on === DISCOUNT_APPLIED_ON.ITEM;

      if (!this.discount && item.discount) {
        item.discount.applied_on = DISCOUNT_APPLIED_ON.ITEM;
      }

      if (
        this.discount &&
        this.discount.type === DISCOUNT_TYPES.PERCENTAGE &&
        item.discount?.type === DISCOUNT_APPLIED_ON.SALE
      ) {
        // item.discount.applied_on = DISCOUNT_APPLIED_ON.ITEM;
        has_odd_discount = true;
      }

      const variation = item.getSelectedVariations();
      let discounts = !!item.discount ? [item.discount] : [];

      if (item.isCustom) {
        discounts = [];
        has_odd_discount = false;
      }

      return {
        // Item
        id: item.id,
        name: item.name,
        tax_on: true,
        tags: item.tags,
        quantity: item.quantity || 1,
        has_odd_discount: has_odd_discount,
        discounts,
        modifiers: item.getSelectedModifiers(),
        variation: {
          id: variation.id,
          name: variation.name,
          type: variation.pricing_type,
          cost: variation.cost,
          price: variation.price_money.amount,
        },

        // Item Origin
        origin: {
          id: item.id,
          name: item.name,
          category: item.category,
          tags: item.tags,
          taxes: item.taxes,
          variations: item?.variations?.map((variation) => ({
            id: variation.id,
            name: variation.name,
            type: variation.pricing_type,
            cost: variation.cost,
            price: variation.price_money.amount,
          })),
          modifier_lists: item?.modifier_lists?.map((list, index: number) => ({
            id: list.id,
            name: list.name,
            type: list.type,
            quantifiable: list.quantifiable,
            sorting: index,
            options: list.modifiers.map((modifier, index) => ({
              id: modifier.id,
              name: modifier.name,
              local_name: modifier.local_name,
              quantity: modifier.quantity || 1,
              cost: modifier.cost,
              price: modifier.price_money.amount,
              sorting: index,
            })),
          })),
        },
      };
    });

    const sale = {
      id: this.sale.id,
      tax_on: true,
      order_option: this.sale.order_option,
      items,
      delivery: this.sale.delivery,
    };

    // Fixed the external_fees (Delivery fees)
    if (this.sale?.order_option?.type === ORDER_OPTION_TYPE.DELIVERY) {
      const external = new ExternalFess({
        id: "00000000-0000-0000-1111-000000000000",
        name: "Delivery",
        type: "FIXED",
        intent: "DELIVERY",
        rate: 0.0,
        amount: this.getDeliveryFeesAmountValue(),
        is_disabled: false,
        is_locked: false,
        tax: null,
      });
      sale["external_fees"] = [external];
    }

    return {
      id: this.id,
      location_id: this.location_id,
      issuer_id: this.issuer_id,
      name: this.name,
      ticket_number: this.ticket_number,
      creation_time: this.creation_time,
      end_time: this.end_time,
      status: this.status,
      total_amount: this.total_amount,
      creator: this.creator,
      sale,
    };
  }

  /***
   * Update Order Total and tax
   */
  public updateTotals(data: any = null) {
    this.total = new PriceMoney({ amount: data?.total || 0 });
    this.additive_tax = new PriceMoney({ amount: data?.additive_tax || 0 });
    this.totalDiscount = new PriceMoney({ amount: data?.discount || 0 });

    let inclusive_tax: number = 0;
    this.sale.items.map((item, index) => {
      let amount = data?.itemization?.[index]?.total || 0;

      if (data?.itemization?.[index]?.taxes?.[0]?.type === TAXES_INCLUSION_TYPE.ADDITIVE) {
        amount = data?.itemization?.[index]?.gross_sales || 0;
      }

      item.totalPrice = new PriceMoney({ amount }); //gross_sales

      data?.itemization?.[index]?.taxes?.forEach((tax) => {
        if (tax?.type === TAXES_INCLUSION_TYPE.INCLUSIVE) {
          inclusive_tax += tax?.applied || 0;
        }
      });
    });

    this.inclusive_tax = new PriceMoney({ amount: inclusive_tax });

    // Sum for all extra charges amount
    let extraChargeAmount = 0;
    data?.extra_charges?.forEach((extra) => {
      // TODD: check the ticket number DV3-613
      extraChargeAmount += extra.applied;
    });
    this.totalExtraCharge = new PriceMoney({ amount: extraChargeAmount });

    setTimeout(() => {
      this.isCalculating = false;
    }, 1000);
  }

  /**
   * Get Tab object
   * return {any}
   */

  /***
   *  has_odd_discount = true << DISCOUNT_APPLIED_ON.ITEM
   *  has_odd_discount = false << DISCOUNT_APPLIED_ON.SALE
   */
  public toCreateTab(): any {
    const tab = this.toTab();

    let items = this.sale.items.map((item) => {
      let has_odd_discount = item?.discount?.applied_on === DISCOUNT_APPLIED_ON.ITEM;

      if (!this.discount && item.discount) {
        item.discount.applied_on = DISCOUNT_APPLIED_ON.ITEM;
      }

      if (
        this.discount &&
        this.discount.type === DISCOUNT_TYPES.PERCENTAGE &&
        item?.discount?.type === DISCOUNT_APPLIED_ON.SALE
      ) {
        // item.discount.applied_on = DISCOUNT_APPLIED_ON.ITEM;
        has_odd_discount = true;
      }
      const variation = item.getSelectedVariations();
      const discounts = !!item.discount ? [item.discount] : [];

      const id = this.isCreateForm() ? Helper.createUUID() : item.id;

      const newItem = {
        // Item
        id,
        name: item.name,
        local_name: item?.local_name,
        tax_on: true,
        tags: item.tags,
        notes: item.notes,
        quantity: item.quantity || 1,
        has_odd_discount: has_odd_discount,
        discounts,
        modifiers: item.getSelectedModifiers(),
        variation: {
          id: variation.id,
          name: variation.name,
          type: variation.pricing_type,
          cost: variation.cost,
          local_name: variation?.local_name,
          price: variation.price_money.amount,
        },

        // Item Origin
        origin: {
          id: item.id,
          name: item.name,
          category: item.category,
          tags: item.tags,
          taxes: item.taxes,
          variations: item?.variations?.map((variation) => ({
            id: variation.id,
            name: variation.name,
            local_name: variation?.local_name,
            type: variation.pricing_type,
            cost: variation.cost,
            price: variation.price_money.amount,
          })),
          modifier_lists: item?.modifier_lists?.map((list, index: number) => ({
            id: list.id,
            name: list.name,
            local_name: list?.local_name,
            type: list.type,
            quantifiable: list.quantifiable,
            order: index,
            options: list.modifiers.map((modifier, index) => ({
              id: modifier.id,
              name: modifier.name,
              local_name: modifier.local_name,
              quantity: modifier.quantity || 1,
              cost: modifier.cost,
              price: modifier.price_money.amount,
              order: index,
            })),
          })),
        },
      };

      // sale item id
      if (!!item.sale_item_id) {
        newItem["sale_item_id"] = item.sale_item_id;
      }

      return newItem;
    });

    tab.sale.customer = this.getSelectedCustomer();
    if (!!tab.sale?.customer?.address) {
      const area = {
        ...tab.sale.customer.address.area,
        name_en: tab.sale.customer.address.area?.en ?? tab.sale.customer.address.area?.name_en,
        name_ar: tab.sale.customer.address.area?.ar ?? tab.sale.customer.address.area?.name_ar,

        city_id: tab.sale.customer.address.city_id,
      };

      const city = {
        ...tab.sale.customer.address.city,
        name_en: tab.sale.customer.address.city?.en || tab.sale.customer.address.city?.name_en,
        name_ar: tab.sale.customer.address.city?.ar || tab.sale.customer.address.city?.name_ar,
      };

      tab.sale.customer.address = {
        ...tab.sale.customer.address,
        area,
        city,
      };

      if (tab.sale.customer?.address?.avenue) {
        const avenue = {
          ...tab.sale.customer.address.avenue,
          area_id: tab.sale.customer.address.area_id,
          name_en: tab.sale.customer.address.avenue.en,
          name_ar: tab.sale.customer.address.avenue.ar,
        };
        tab.sale.customer.address.avenue = {
          ...avenue,
        };
      }

      if (tab.sale.customer?.address?.block) {
        const block = {
          ...tab.sale.customer?.address?.block,
          avenue_id: tab.sale.customer.address.avenue.id,
          name_en: tab.sale.customer.address.block.en,
          name_ar: tab.sale.customer.address.block.ar,
        };

        tab.sale.customer.address.block = {
          ...block,
        };
      }
    }

    tab.total_amount = this.total?.amount;
    tab.payment_methods = this.payment_methods || [];
    tab.location_id = this.location_id;
    tab.sale.items = [...items];
    tab.comments = this.comments;
    tab.app_id = this.app_id;
    tab.notes = this.notes;

    // Fixed the external_fees (Delivery fees)
    if (tab.sale?.order_option?.type === ORDER_OPTION_TYPE.DELIVERY) {
      const external_fees = new ExternalFess({
        id: "00000000-0000-0000-1111-000000000000",
        name: "Delivery",
        type: "FIXED",
        intent: "DELIVERY",
        rate: 0.0,
        amount: this.getDeliveryFeesAmountValue(),
        is_disabled: false,
        is_locked: false,
        tax: null,
      });
      tab.sale["external_fees"] = [external_fees];
    }
    tab.status = this.status;
    tab.source = this.source;

    if (this.isCreateForm() || this.isReorder) {
      tab.name = `CC#${this.orderNumber}-${this.orderName || this.sale?.customer?.fullName()}`.substr(0, 50);
      tab.ticket_number = `${this.orderNumber}`;
      tab.creation_time = moment().toISOString();
    }

    //
    // if (this.isEditForm() && !this.isReorder) {
    //   tab.name = `CC#${this.ticket_number}-${this.orderName || this.sale?.customer?.fullName()}`;
    // }

    // For testing
    // tab.end_time = new Date(); //this.status;
    // tab.status = ORDER_STATUS.CLOSED; //this.status;
    // tab.status = ORDER_STATUS.OPENED;
    // tab.status = ORDER_STATUS.CANCELED;
    // tab.status = ORDER_STATUS.REJECTED;
    return tab;
  }

  /**
   * Save Failed Order
   * @return {Promise<void>}
   */
  public async storeFailedOrder() {
    return await new Promise((resolve) => {
      const list = Storage.get(StorageConstants.FAILED_ORDERS_LIST) || [];
      const index = list.findIndex((item) => item.id === this.id);
      this.isFailed = true;
      this.retryCount += 1;
      this.failedTime = new Date().toString();

      if (index < 0) {
        list.push(this);
      }

      Storage.set(StorageConstants.FAILED_ORDERS_LIST, list);
      return resolve(this);
    });
  }

  /**
   * Implement Discount
   * @return {void}
   */
  public setSuitableDiscount(): void {
    // sale discount
    const discount = this.discount;

    if (
      (discount?.automatic || discount?.type === DISCOUNT_TYPES.FIXED) &&
      discount?.type !== DISCOUNT_TYPES.PERCENTAGE
    ) {
      const newItemDiscount = new ItemDiscount(null);
      Object.assign(newItemDiscount, discount);

      this.sale.items.map((item) => {
        if (!item.isCustom) {
          item.discount = newItemDiscount;
        }
      });
      return;
    }

    if (discount?.type === DISCOUNT_TYPES.PERCENTAGE) {
      this.sale.items.map((item) => {
        if (!item?.discount && !item.isCustom) {
          const newItemDiscount = new ItemDiscount(null);
          Object.assign(newItemDiscount, discount);
          newItemDiscount.applied_on = DISCOUNT_APPLIED_ON.SALE;
          item.discount = new ItemDiscount(newItemDiscount);
        }
      });

      return;
    }
  }

  public destroy(): void {
    delete this.sale;
    delete this.notes;
    delete this.order_option;
    delete this.total;
    delete this.additive_tax;
    delete this.inclusive_tax;
    delete this.totalDiscount;
    delete this.totalExtraCharge;
    delete this.discount;
    delete this.creator;
  }
}
